import { parse } from "node-html-parser";
import React, { Fragment, VFC } from "react";
import styled from "styled-components";
import { TableOfContentBase } from "~/components/modules/toc/TocContent";
import { QuestionAnswer } from "~/models/questionAnswer";

interface AmpTocContentProps {
  answerTableOfContentHtml: QuestionAnswer["answerTableOfContentHtml"];
}

const VIEWPORT_MARGINS = "0 75vh";
const INTERSECTION_RATIOS = "1";

const AmpTocContent: VFC<AmpTocContentProps> = ({
  answerTableOfContentHtml,
}) => {
  if (!answerTableOfContentHtml) {
    return null;
  }

  const { html, sectionIds } = generateModifiedTocData({
    html: answerTableOfContentHtml,
  });

  const generateAmpTagsForScrollDetection = () => {
    return sectionIds.map((sectionId) => {
      const visibleAnimationId = `is-visible-${sectionId}`;
      const hideAnimationId = `is-hidden-${sectionId}`;
      const otherSectionIds = sectionIds.filter((id) => id !== sectionId);

      return (
        <Fragment key={sectionId}>
          <amp-position-observer
            target={sectionId}
            viewport-margins={VIEWPORT_MARGINS}
            intersection-ratios={INTERSECTION_RATIOS}
            on={`enter:${otherSectionIds
              .map((id) => `is-hidden-${id}.start`)
              .join(",")}`}
            layout="nodisplay"
          ></amp-position-observer>

          <amp-position-observer
            target={sectionId}
            viewport-margins={VIEWPORT_MARGINS}
            intersection-ratios={INTERSECTION_RATIOS}
            on={`enter:${visibleAnimationId}.start;`}
            layout="nodisplay"
          ></amp-position-observer>

          <amp-animation id={visibleAnimationId} layout="nodisplay">
            <script
              type="application/json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  animations: [
                    {
                      keyframes: [
                        {
                          opacity: "1",
                        },
                      ],
                      selector: `a[href="#${sectionId}"] .toc-active-text`,
                    },
                    {
                      keyframes: [
                        {
                          opacity: "0",
                        },
                      ],
                      selector: `a[href="#${sectionId}"] .toc-normal-text`,
                    },
                  ],
                  direction: "alternate",
                  fill: "both",
                  iterations: "1",
                }),
              }}
            />
          </amp-animation>
          <amp-animation id={hideAnimationId} layout="nodisplay">
            <script
              type="application/json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  animations: [
                    {
                      keyframes: [
                        {
                          opacity: "0",
                        },
                      ],
                      selector: `a[href="#${sectionId}"] .toc-active-text`,
                    },
                    {
                      keyframes: [
                        {
                          opacity: "1",
                        },
                      ],
                      selector: `a[href="#${sectionId}"] .toc-normal-text`,
                    },
                  ],
                  direction: "alternate",
                  fill: "both",
                  iterations: "1",
                }),
              }}
            />
          </amp-animation>
        </Fragment>
      );
    });
  };

  return (
    <>
      <TableOfContent dangerouslySetInnerHTML={{ __html: html }} />
      {generateAmpTagsForScrollDetection()}
    </>
  );
};

function generateModifiedTocData({ html }: { html: string }): {
  sectionIds: string[];
  html: string;
} {
  const dom = parse(html);

  const links = dom.querySelectorAll("a");

  const sectionIds = links.map(
    (link) => link.getAttribute("href")?.replace("#", "") ?? ""
  );

  links.forEach((link) => {
    const text = link.innerText;

    link.innerHTML = `
      <span class="toc-normal-text">${text}</span>
      <span class="toc-active-text">${text}</span>
    `;
  });

  return {
    html: dom.toString(),
    sectionIds,
  };
}

const TableOfContent = styled(TableOfContentBase)`
  > ol > li > a {
    .toc-active-text {
      font-weight: 700;
    }
  }

  .toc-active-text {
    color: var(--b-clr900);
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;

    &::before {
      background-color: var(--pri-clr500);
      border-radius: 9999px;
      border: 2px solid var(--box-bdr-clr);
      box-sizing: border-box;
      content: "";
      display: block;
      height: 12px;
      left: -20px;
      position: absolute;
      top: 6px;
      width: 12px;
    }
  }
`;

export default AmpTocContent;
